import type { AppState } from '@xuwo/drawing/types';

import { getArrangementGroups } from './arrange';
import { updateBoundElements } from './binding';
import type { BoundingBox } from './bounds';
import { getCommonBoundingBox } from './bounds';
import type { Scene } from './Scene';
import type { ExcalidrawElement, NonDeletedExcalidrawElement } from './types';

export interface Alignment {
  position: 'start' | 'center' | 'end';
  axis: 'x' | 'y';
}

export const alignElements = (
  selectedElements: NonDeletedExcalidrawElement[],
  alignment: Alignment,
  scene: Scene,
  appState: Readonly<Pick<AppState, 'selectedGroupIds' | 'editingGroupId'>>
): NonDeletedExcalidrawElement[] => {
  const groups = getArrangementGroups(
    selectedElements,
    scene.getNonDeletedElementsMap(),
    appState
  );
  const selectionBoundingBox = getCommonBoundingBox(
    groups.flatMap(group => group.boundsElements)
  );

  return groups.flatMap(({ elements: group, boundsElements }) => {
    const translation = calculateTranslation(
      boundsElements,
      selectionBoundingBox,
      alignment
    );
    return group.map(element => {
      // update element
      const updatedEle = scene.mutateElement(element, {
        x: element.x + translation.x,
        y: element.y + translation.y,
      });

      // update bound elements
      updateBoundElements(element, scene, {
        simultaneouslyUpdated: group,
      });
      return updatedEle;
    });
  });
};

const calculateTranslation = (
  group: readonly ExcalidrawElement[],
  selectionBoundingBox: BoundingBox,
  { axis, position }: Alignment
): { x: number; y: number } => {
  const groupBoundingBox = getCommonBoundingBox(group);

  const [min, max]: ['minX' | 'minY', 'maxX' | 'maxY'] =
    axis === 'x' ? ['minX', 'maxX'] : ['minY', 'maxY'];

  const noTranslation = { x: 0, y: 0 };
  if (position === 'start') {
    return {
      ...noTranslation,
      [axis]: selectionBoundingBox[min] - groupBoundingBox[min],
    };
  } else if (position === 'end') {
    return {
      ...noTranslation,
      [axis]: selectionBoundingBox[max] - groupBoundingBox[max],
    };
  } // else if (position === "center") {
  return {
    ...noTranslation,
    [axis]:
      (selectionBoundingBox[min] + selectionBoundingBox[max]) / 2 -
      (groupBoundingBox[min] + groupBoundingBox[max]) / 2,
  };
};
